import { useState } from 'react';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { IconRemove } from '@components/icons/remove';
import { hideModal, Modal, useModal } from '@components/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useCancelJourneyChanges } from 'api/journeys/hooks';

export const useCancelChangesModal = () => useModal(CancelChanges);

export const CancelChanges = ({ journeyShortId }: { journeyShortId: string }) => {
  const { t } = useScopedTranslate(locale);
  const [confirm, setConfirm] = useState(false);

  const cancelChanges = useCancelJourneyChanges({ onSuccess: hideModal });

  return (
    <Modal>
      <Modal.SimpleHeader title={t('cancelChanges')} />
      <Modal.Content>
        <Stack space={6}>
          <Stack>
            <Text text={t('areYouSure')} />
            <Text color="elementRed100" text={t('cannotBeUndone')} />
          </Stack>
          <Separator />
          <Stack space={4}>
            <Text font="semibold14" text={t('pleaseConfirm')} />
            <Checkbox label={t('iUnderstand')} onChange={(value) => setConfirm(value)} />
          </Stack>
          <Stack orientation="horizontal" space={4} justify="flex-end">
            <Button variant="secondary" onClick={hideModal} text={t('core.cancel')} />
            <Button
              isDisabled={!confirm}
              onClick={() => cancelChanges.mutateAsync(journeyShortId)}
              disabled={cancelChanges.isLoading}
              variant="primary-red"
              iconLeft={<IconRemove />}
              text={t('remove')}
            />
          </Stack>
        </Stack>
      </Modal.Content>
    </Modal>
  );
};

export const locale = {
  en: {
    cancelChanges: 'Cancel changes',
    areYouSure:
      'Are you sure you want to cancel the changes that have been made since the journey was last published?',
    cannotBeUndone: 'This can not be undone.',
    pleaseConfirm: 'Please confirm',
    iUnderstand: 'I understand',
    remove: 'Confirm',
  },
};
